import React, { useState } from "react";
import {
    Row,
    Col,
    Form,
    Input,
    Label,
    CardBody,
    Modal,
    ModalHeader,
    ModalBody,
} from "reactstrap";
import PropTypes from "prop-types";
import withRouter from "../Components/withRouter";
import * as Yup from "yup";
import { useFormik } from "formik";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from "../Media/LogoLogin.png";
import CarouselPage from "../Components/CarouselPage";
import { errorToast, infoToast, successToast } from "../Components/Toasts";
import { getAuthData, setAuthData } from "../utils/auth/authUtils";
import env from '../environment_system/env_system';
import { encrypt } from "GenericForms/Helper";
import apiGlobal from "global/api.global";
import { useDocumentTitle } from "Components/useDocument.hooks";
import ErrorTooltip from "Components/ErrorTooltip";

const Login = () => {
    /** State variables start */
    useDocumentTitle('Login - ecoSAIL');
    const [PasswordType, setPasswordType] = useState<'password' | 'text'>('password');
    const [blockedAlert, setBlockedAlert] = useState(false);
    /** State variables end */

    /** Login's formik object */
    const LoginFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            username: "",
            password: "",
            checkbox: false,
        },
        validationSchema: Yup.object({
            username: Yup.string().required("Please Enter Your Email")
                .test(function (value: any) {
                    if (value.includes('@')) {
                        if (!(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value))) {
                            return new Yup.ValidationError("Please enter a valid email id", undefined, "username");
                        }
                    } else if (!(/^[A-Za-z][A-Za-z0-9_]{5,}$/.test(value))) {
                        return new Yup.ValidationError(`
                                - Must be at least 6 characters long\n
                                - Must start with a letter\n
                                - Can only contain letters and digits (no special characters except _ or spaces)`
                            , undefined, "username");
                    }
                    return true;
                }),
            password: Yup.string().min(6, "Password must be at least 6 characters long").required("Please enter your password"),
        }),
        onSubmit: async (values) => {
            LoginFormik.setSubmitting(true);
            infoToast("Checking Logging! Please wait!");
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            let verification = false;
            const encryptedPassword = encrypt(values?.password);
            await apiGlobal.post('/jwt-login/', {
                email_or_username: values?.username,
                password: encryptedPassword,
            }).then(async (response: any) => {
                if (response.status === 200) {
                    setBlockedAlert(false);
                    verification = true;
                    // eslint-disable-next-line no-restricted-syntax
                    await setAuthData(JSON.stringify(response.data));
                    if (getAuthData()) {
                        successToast("Logged in successfully!");
                        window.location.href = "/dashboard";
                    }
                } else {
                    verification = false;
                }
                return response.data;
            }).catch((error: any) => {
                if (error?.response?.data?.status[0] === "403") {
                    setBlockedAlert(true);
                } else if (error?.response?.data) {
                    errorToast(error?.response?.data?.error[0]);
                }
            })
            LoginFormik.setSubmitting(false);
        },
    });

    // const { error } = useSelector((state: any) => ({
    //     error: state.login.error,
    // }));
    const toggle = () => setBlockedAlert(!blockedAlert);
    return (
        <React.Fragment>
            <div className="auth-page">
                {/* <Container fluid className="p-0"> */}
                <Row className="g-0">
                    <Col lg={4} md={5} className="col-xxl-3">
                        <div className="auth-full-page-content d-flex p-sm-5 p-4">
                            <div className="w-100">
                                <div className="d-flex flex-column h-100">
                                    <div className="mb-4 mb-md-5 text-center">
                                        <div className="d-block auth-logo">
                                            <img src={logo} alt="" height="140" />{" "}
                                        </div>
                                    </div>
                                    <Form
                                        className="custom-form mt-4 pt-2"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            LoginFormik.handleSubmit();
                                        }}
                                        autoComplete="off"
                                        noValidate
                                    >
                                        {/* {error ? <Alert color="danger">{error}</Alert> : null} */}
                                        <div className="mb-3">
                                            <Label className="form-label mb-0" htmlFor="username">Email / Username</Label>
                                            <Input
                                                onFocus={(e) => e.target.select()}
                                                name="username"
                                                id="username"
                                                className="form-control"
                                                type="email"
                                                onChange={LoginFormik.handleChange}
                                                onBlur={LoginFormik.handleBlur}
                                                defaultValue={LoginFormik.values.username}
                                            />
                                            {LoginFormik?.errors?.username && LoginFormik?.touched?.username && env?.form_validation === true &&
                                                <ErrorTooltip
                                                    target={`username`}
                                                    message={LoginFormik?.errors?.username.split('\n').map((msg, idx) => (
                                                        <div key={idx}>{msg}</div>
                                                    ))}
                                                    open={(LoginFormik?.errors && LoginFormik?.errors?.username) ? true : false}
                                                />
                                            }
                                        </div>
                                        <Label className="form-label mb-0" htmlFor="password">Password</Label>
                                        <div className="mb-3 position-relative">
                                            <Input
                                                onFocus={(e) => e.target.select()}
                                                name="password"
                                                id="password"
                                                className="form-control"
                                                defaultValue={LoginFormik.values.password}
                                                type={PasswordType}
                                                onChange={LoginFormik.handleChange}
                                                onBlur={LoginFormik.handleBlur}
                                            />
                                            <i
                                                className={`mdi ${PasswordType === 'password' ? 'mdi-eye' : 'mdi-eye-off'} eye`}
                                                onClick={() => setPasswordType(PasswordType === 'password' ? 'text' : 'password')}
                                            />
                                            {LoginFormik?.errors?.password && LoginFormik?.touched?.password && env?.form_validation === true &&
                                                <ErrorTooltip
                                                    target={`password`}
                                                    message={LoginFormik?.errors?.password}
                                                    open={(LoginFormik?.errors && LoginFormik?.errors?.password) ? true : false}
                                                />
                                            }
                                        </div>
                                        <div className="row mb-4">
                                            <div className="col">
                                                <div className="mt-3 d-grid">
                                                    <button className="primary btn btn-primary" disabled={LoginFormik.isSubmitting} type="submit">
                                                        {
                                                            LoginFormik.isSubmitting ?
                                                                <React.Fragment>
                                                                    <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                                                    Loading...
                                                                </React.Fragment> :
                                                                'Log In'
                                                        }
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <Modal isOpen={blockedAlert} toggle={toggle} centered>
                                            <ModalHeader className="bg-transparent border-danger text-danger">
                                                Notification
                                            </ModalHeader>
                                            <ModalBody>
                                                <div className="pt-2 pb-2" >
                                                    <CardBody>
                                                        <h5 className="card-title text-danger">
                                                            Account Blocked
                                                        </h5>
                                                        <p className="card-text text-danger">
                                                            Your account is blocked. Contact your manager for further details.
                                                        </p>
                                                    </CardBody>
                                                </div>
                                            </ModalBody>
                                        </Modal>
                                        {/*{env?.desktop_app === true &&
                                            verification === true &&
                                            <>
                                             <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="remember-check"
                                                        name="checkbox"
                                                        onChange={(e: any) => validation.handleChange(e)}
                                                    />
                                                    <label
                                                        className="form-check-label"
                                                        htmlFor="remember-check"
                                                    >
                                                        Check this to continue
                                                    </label>
                                                </div>
                                                <Row className="mt-3">
                                                    {validation.values.checkbox === false &&
                                                        <Navigate to={"/dashboard"} />
                                                    }
                                                </Row>
                                            </>
                                        } */}
                                    </Form>
                                    <div className="mt-4 mt-md-5 text-center">
                                        <p className="mb-0">
                                            © {new Date().getFullYear()}. Developed by{" "}
                                            <a href="https://ecosailinfotech.com/" target="_blank" rel="noopener noreferrer">
                                                ecoSAIL
                                            </a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <CarouselPage />
                </Row>
                {/* </Container> */}
            </div >
            <ToastContainer />
        </React.Fragment >
    );
};

export default withRouter(Login);
Login.propTypes = {
    history: PropTypes.object,
};


